"use strict";

import { onReady } from "./ready.js";

const INITED_ATTR = "data-autoplay-video-inited";
const OBSERVER_OPTIONS = {
    root: null,
    rootMargin: "0px",
    threshold: 0.25,
};

function isExcluded(video) {
    if (video.closest(".modal")) return true;
    if (video.dataset.videoAutoplay === "off") return true;
    if (video.classList.contains("block-video") && video.closest('[data-hero-video-modal="1"]')) {
        return true;
    }
    return false;
}

function isCarouselSlideVideo(video) {
    return video.classList.contains("slide-video");
}

function isCarouselSlideActive(video) {
    const slide = video.closest(".carousel-item");
    return slide?.classList.contains("active") ?? false;
}

function configureVideo(video) {
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    video.setAttribute("loop", "");
}

function safePlay(video) {
    if (!shouldPlayNow(video)) return;
    const promise = video.play();
    if (promise?.catch) {
        promise.catch(() => {});
    }
}

function safePause(video) {
    if (!video.paused) {
        video.pause();
    }
}

function shouldPlayNow(video) {
    if (isCarouselSlideVideo(video) && !isCarouselSlideActive(video)) {
        return false;
    }
    return true;
}

function isElementInViewport(el) {
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;
    const viewH = window.innerHeight || document.documentElement.clientHeight || 0;
    return rect.bottom > 0 && rect.top < viewH;
}

function syncCarouselSlideVideos(carousel) {
    carousel.querySelectorAll(".slide-video").forEach((video) => {
        if (isExcluded(video)) return;
        configureVideo(video);
        if (isCarouselSlideActive(video) && isElementInViewport(video)) {
            if (video.readyState < 2) {
                video.load();
            }
            safePlay(video);
        } else {
            safePause(video);
        }
    });
}

let observer = null;
let carouselBound = false;

function bindCarouselHandlers() {
    if (carouselBound) return;
    carouselBound = true;

    document.querySelectorAll(".carousel").forEach((carousel) => {
        carousel.addEventListener("slid.bs.carousel", () => {
            syncCarouselSlideVideos(carousel);
        });
    });
}

function observeVideo(video) {
    if (video.getAttribute(INITED_ATTR) === "1") return;
    video.setAttribute(INITED_ATTR, "1");
    configureVideo(video);
    observer.observe(video);
}

export function initAutoplayVideos(root = document) {
    const videos = [...root.querySelectorAll("video")].filter((video) => !isExcluded(video));
    if (!videos.length) return;

    bindCarouselHandlers();

    if (!("IntersectionObserver" in window)) {
        videos.forEach((video) => {
            video.setAttribute(INITED_ATTR, "1");
            configureVideo(video);
            if (isElementInViewport(video)) {
                if (video.readyState < 2) {
                    video.load();
                }
                safePlay(video);
            }
        });
        return;
    }

    if (!observer) {
        observer = new IntersectionObserver((entries) => {
            entries.forEach(({ target, isIntersecting }) => {
                if (isIntersecting) {
                    if (target.readyState < 2) {
                        target.load();
                    }
                    safePlay(target);
                } else {
                    safePause(target);
                }
            });
        }, OBSERVER_OPTIONS);
    }

    videos.forEach(observeVideo);
}

export function refreshAutoplayVideos() {
    document.querySelectorAll("video").forEach((video) => {
        if (isExcluded(video)) return;
        configureVideo(video);
        if (isElementInViewport(video)) {
            if (video.readyState < 2) {
                video.load();
            }
            safePlay(video);
        } else {
            safePause(video);
        }
    });
}

onReady(() => initAutoplayVideos());

window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
        refreshAutoplayVideos();
    }
});
